// Ref: GitHub issue #37, task A3 — error boundary
import React, { Component, ErrorInfo, ReactNode } from 'react';

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Optional custom fallback UI. If omitted the built-in friendly error card is shown. */
  fallback?: (error: Error, retry: () => void) => ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * ErrorBoundary — class component that catches unhandled render errors in its
 * subtree and shows a friendly error UI with a retry option.
 *
 * Usage:
 *   <ErrorBoundary>
 *     <SomeComponent />
 *   </ErrorBoundary>
 *
 * With custom fallback:
 *   <ErrorBoundary fallback={(err, retry) => <MyFallback err={err} onRetry={retry} />}>
 *     …
 *   </ErrorBoundary>
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
    this.handleRetry = this.handleRetry.bind(this);
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Log to console in development; wire up to a real error-reporting service here.
    console.error('[ErrorBoundary] Caught error:', error, info.componentStack);
  }

  handleRetry() {
    this.setState({ hasError: false, error: null });
  }

  render() {
    const { hasError, error } = this.state;
    const { children, fallback } = this.props;

    if (!hasError) return children;

    if (fallback && error) {
      return fallback(error, this.handleRetry);
    }

    return (
      <div
        role="alert"
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '40vh',
          padding: 'var(--space-6)',
          textAlign: 'center',
          gap: 'var(--space-4)',
        }}
      >
        <span style={{ fontSize: '3rem' }} aria-hidden="true">😕</span>

        <div>
          <h2
            style={{
              fontSize: 'var(--text-xl)',
              fontWeight: 'var(--font-weight-semibold)',
              color: 'var(--color-text-primary)',
              marginBottom: 'var(--space-2)',
            }}
          >
            Something went wrong
          </h2>
          <p
            style={{
              fontSize: 'var(--text-sm)',
              color: 'var(--color-text-secondary)',
              maxWidth: 420,
            }}
          >
            An unexpected error occurred. You can try again — if the problem
            persists, please contact support.
          </p>
        </div>

        <button
          className="btn btn-primary"
          onClick={this.handleRetry}
        >
          Try again
        </button>
      </div>
    );
  }
}
